import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  EMPTY,
  finalize,
  forkJoin,
  from,
  mergeMap,
  pipe,
  switchMap,
  exhaustMap,
  filter,
  tap,
} from 'rxjs';

import { AlertStore } from '@alert/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';

import {
  CloseProfileSubscriptionPayload,
  CreateConsumptionReleasePayload,
  DeleteConsumptionReleasePayload,
  ProfileConsumptionRelease,
  ProfileSubscriptionResponse,
  ProfileSubscriptionListItem,
  RenewProfileSubscriptionPayload,
  AddPauseProfileSubscriptionPayload,
  UpdatePauseProfileSubscriptionPayload,
  DeletePauseProfileSubscriptionPayload,
  ProfileSubscriptionPause,
  SwitchProfileSubscriptionPayload,
  SwapOwnershipProfileSubscriptionPayload,
  TransferProfileSubscriptionPayload,
  ESubscriptionsByPersonFieldFilter,
  ProfileOpenReceivable,
} from './entities/profile-subscription.model';
import {
  TChangeFinancialResponsibleRequest,
  TChangeFinancialResponsibleResponse,
} from './entities/financial-responsible.model';
import { ProfileSubscriptionService } from './infra/profile-subscription.service';
import {
  SelfUserStore,
  PermissionNumber,
} from '@domains/person/user/self/data';
import { TSubscriptionPause } from '@subscription/data-access';
import { DateTime } from 'luxon';
import { getDateTime } from '@shared/utils';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

/**
 * Quantos preloads de `Dependents` podem correr em paralelo. Uma pessoa com
 * muitas assinaturas titulares dispararia um GET por card de uma vez só; o teto
 * mantém o paralelismo sem afogar a conexão.
 */
const DEPENDENTS_FLAG_PRELOAD_CONCURRENCY = 4;

/**
 * Mesmo teto para os preloads de recebíveis em aberto: um GET por card de
 * assinatura, todos disparados no mesmo tick.
 */
const OPEN_RECEIVABLES_PRELOAD_CONCURRENCY = 4;

/**
 * Same ceiling for the card's release lookups: one GET per overdue subscription,
 * fired as soon as its receivables arrive.
 */
const CONSUMPTION_RELEASES_PRELOAD_CONCURRENCY = 4;

export type ChangeFinancialResponsiblePayload = {
  subscriptionId: string;
  request: TChangeFinancialResponsibleRequest;
};

export type DeleteSubscriptionByIdPayload = {
  subscriptionId: string;
  hasReceivement: boolean;
};

export interface IProfileSubscriptionStore {
  personId: string | null;
  lastCreatedSubscription: ProfileSubscriptionResponse | null;
  lastUpdatedSubscription: ProfileSubscriptionResponse | null;
  lastPatchedSubscription: ProfileSubscriptionResponse | null;
  lastRenewedSubscription: ProfileSubscriptionResponse | null;
  lastClosedSubscription: ProfileSubscriptionResponse | null;
  lastPausedSubscription: ProfileSubscriptionResponse | null;
  lastSwitchedSubscription: ProfileSubscriptionResponse | null;
  lastTransferredSubscription: ProfileSubscriptionResponse | null;
  lastSwapOwnershipSubscription: ProfileSubscriptionResponse | null;
  currentSubscription: ProfileSubscriptionResponse | null;
  lastDeletedSubscriptionId: string | null;
  lastDeletedSubscriptionRenewalsId: string | null;
  lastDeletedSubscriptionInstallmentsId: string | null;
  subscriptions: ProfileSubscriptionListItem[];
  subscriptionPauses: ProfileSubscriptionPause[];
  consumptionReleases: ProfileConsumptionRelease[];
  subscriptionFilter: number;
  /**
   * Result of the last `checkActiveOrPausedSubscriptions` run: `true` when the
   * person still has at least one active or paused subscription, `false` when
   * none, `null` before the check has resolved.
   */
  hasActiveOrPausedSubscriptions: boolean | null;
  /**
   * Map from SubscriptionId to "has at least one dependent?" flag, populated
   * lazily when a holder card preloads the detail. Avoids overwriting the
   * single-slot `currentSubscription` per card.
   */
  subscriptionHasDependentsById: Record<string, boolean>;
  /** Map from SubscriptionId to its open account receivables (OData feed). */
  openReceivablesBySubscriptionId: Record<string, ProfileOpenReceivable[]>;
  /**
   * Map from SubscriptionId to its consumption releases, for the card. Kept apart
   * from `consumptionReleases`, the single slot the releases modal owns.
   */
  consumptionReleasesBySubscriptionId: Record<
    string,
    ProfileConsumptionRelease[]
  >;
  /**
   * Answer of the last `cancelAutomaticRenewal` (the subscription with
   * `DeletedRenewals`); null before it succeeds.
   */
  lastAutoRenewalCanceledSubscription: ProfileSubscriptionResponse | null;
  /** Resposta da última troca de responsável; abre a modal de resultado. */
  lastFinancialResponsibleChange: TChangeFinancialResponsibleResponse | null;
  /**
   * A troca voltou 404 (assinatura não existe mais) ou 409 (encerrada): a tela
   * fecha a modal e recarrega a lista. `null` fora desses casos.
   */
  financialResponsibleGone: 'notFound' | 'closed' | null;
}

export const initialProfileSubcriptionStore: IProfileSubscriptionStore = {
  personId: null,
  lastCreatedSubscription: null,
  lastUpdatedSubscription: null,
  lastPatchedSubscription: null,
  lastRenewedSubscription: null,
  lastClosedSubscription: null,
  lastPausedSubscription: null,
  lastSwitchedSubscription: null,
  lastTransferredSubscription: null,
  lastSwapOwnershipSubscription: null,
  currentSubscription: null,
  lastDeletedSubscriptionId: null,
  lastDeletedSubscriptionRenewalsId: null,
  lastDeletedSubscriptionInstallmentsId: null,
  subscriptions: [],
  subscriptionPauses: [],
  consumptionReleases: [],
  subscriptionFilter: 0,
  hasActiveOrPausedSubscriptions: null,
  subscriptionHasDependentsById: {},
  openReceivablesBySubscriptionId: {},
  consumptionReleasesBySubscriptionId: {},
  lastAutoRenewalCanceledSubscription: null,
  lastFinancialResponsibleChange: null,
  financialResponsibleGone: null,
};
export const ProfileSubscriptionStore = signalStore(
  { providedIn: 'root' },
  withState<IProfileSubscriptionStore>(initialProfileSubcriptionStore),
  withMethods(
    (
      store,
      profileSubscriptionService = inject(ProfileSubscriptionService),
      alertStore = inject(AlertStore),
      errorStore = inject(ErrorStore),
      selfUserStore = inject(SelfUserStore)
    ) => {
      const renewSubscription = rxMethod<RenewProfileSubscriptionPayload>(
        pipe(
          tap(() =>
            patchState(store, {
              lastRenewedSubscription: null,
              ...setPropLoading('renewSubscription'),
            })
          ),
          switchMap((payload) => {
            if (!payload?.SubscriptionId) {
              const error = new Error('Missing SubscriptionId for renew');
              patchState(store, {
                ...setPropError('renewSubscription', error),
              });
              errorStore.handleError({
                error,
                title:
                  'Não foi possível identificar a assinatura para renovar.',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaRenovar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RENEW.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RENEW.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    lastRenewedSubscription: null,
                    ...setPropError(
                      'renewSubscription',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .renewSubscription(payload)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        patchState(store, {
                          lastRenewedSubscription: response,
                          ...setPropLoaded('renewSubscription'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('renewSubscription', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      const closeSubscription = rxMethod<CloseProfileSubscriptionPayload>(
        pipe(
          tap(() =>
            patchState(store, {
              lastClosedSubscription: null,
              ...setPropLoading('closeSubscription'),
            })
          ),
          switchMap((payload) => {
            if (!payload?.SubscriptionId) {
              const error = new Error('Missing SubscriptionId for close');
              patchState(store, {
                ...setPropError('closeSubscription', error),
              });
              errorStore.handleError({
                error,
                title:
                  'Não foi possível identificar a assinatura para encerrar.',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.EncerrarAssinatura],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_CLOSE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_CLOSE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    lastClosedSubscription: null,
                    ...setPropError(
                      'closeSubscription',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .closeSubscription(payload)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        alertStore.create({
                          message: 'SUBSCRIPTION.ALERTS.CLOSE.SUCCESSFFULLY',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastClosedSubscription: response,
                          ...setPropLoaded('closeSubscription'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('closeSubscription', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      const deleteSubscriptionById = rxMethod<DeleteSubscriptionByIdPayload>(
        pipe(
          filter((payload) => Boolean(payload?.subscriptionId)),
          tap(() =>
            patchState(store, { ...setPropLoading('deleteSubscriptionById') })
          ),
          // `hasReceivement` default guards against a dynamically-typed Observable/Signal emitting a malformed payload; typed call sites always supply an explicit boolean.
          switchMap(({ subscriptionId, hasReceivement = false }) => {
            const requiredPermissions = hasReceivement
              ? [
                  PermissionNumber.AssinaturaPessoaRemover,
                  PermissionNumber.AssinaturaPessoaRemoverComRecebimento,
                ]
              : [PermissionNumber.AssinaturaPessoaRemover];
            // Names the receivement permission so the operator knows which
            // one the authorizing user needs.
            const guardKey = hasReceivement
              ? 'SUBSCRIPTION_PERSON_DELETE_WITH_RECEIVEMENT'
              : 'SUBSCRIPTION_PERSON_DELETE';

            return from(
              selfUserStore.ensurePermissions(requiredPermissions, {
                title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${guardKey}.TITLE`,
                message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${guardKey}.MESSAGE`,
              })
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    ...setPropError(
                      'deleteSubscriptionById',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .deleteSubscriptionById(subscriptionId)
                  .pipe(
                    tapResponse({
                      next: () => {
                        alertStore.create({
                          message: 'SUBSCRIPTION.ALERTS.REMOVED.SUCCESSFFULLY',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastDeletedSubscriptionId: subscriptionId,
                          currentSubscription: null,
                          ...setPropLoaded('deleteSubscriptionById'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('deleteSubscriptionById', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      const deleteSubscriptionRenewals = rxMethod<string>(
        pipe(
          filter(Boolean),
          tap(() =>
            patchState(store, {
              lastDeletedSubscriptionRenewalsId: null,
              ...setPropLoading('deleteSubscriptionRenewals'),
            })
          ),
          switchMap((subscriptionId) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaRemover],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_DELETE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_DELETE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    ...setPropError(
                      'deleteSubscriptionRenewals',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .deleteSubscriptionRenewals(subscriptionId)
                  .pipe(
                    tapResponse({
                      next: () => {
                        alertStore.create({
                          message: 'Renovações excluídas com sucesso.',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastDeletedSubscriptionRenewalsId: subscriptionId,
                          ...setPropLoaded('deleteSubscriptionRenewals'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('deleteSubscriptionRenewals', error),
                        });
                      },
                    })
                  );
              })
            )
          )
        )
      );

      /**
       * "Excluir renovações" of a finished subscription with automatic renewal
       * scheduled. Unlike `deleteSubscriptionRenewals`, it also turns the
       * automatic renewal off, so the job does not renew again.
       */
      const cancelAutomaticRenewal = rxMethod<string>(
        pipe(
          filter(Boolean),
          tap(() =>
            patchState(store, {
              lastAutoRenewalCanceledSubscription: null,
              ...setPropLoading('cancelAutomaticRenewal'),
            })
          ),
          switchMap((subscriptionId) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaAtualizar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    ...setPropError(
                      'cancelAutomaticRenewal',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .cancelAutomaticRenewal(subscriptionId)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        alertStore.create({
                          message:
                            'SUBSCRIPTION.ALERTS.CANCEL_AUTOMATIC_RENEWAL.SUCCESSFULLY',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastAutoRenewalCanceledSubscription: response,
                          ...setPropLoaded('cancelAutomaticRenewal'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('cancelAutomaticRenewal', error),
                        });
                      },
                    })
                  );
              })
            )
          )
        )
      );

      const deleteSubscriptionInstallments = rxMethod<string>(
        pipe(
          filter(Boolean),
          tap(() =>
            patchState(store, {
              lastDeletedSubscriptionInstallmentsId: null,
              ...setPropLoading('deleteSubscriptionInstallments'),
            })
          ),
          switchMap((subscriptionId) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaRemover],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_DELETE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_DELETE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    ...setPropError(
                      'deleteSubscriptionInstallments',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .deleteSubscriptionInstallments(subscriptionId)
                  .pipe(
                    tapResponse({
                      next: () => {
                        alertStore.create({
                          message:
                            'SUBSCRIPTION.ALERTS.DELETE_INSTALLMENTS.SUCCESSFFULLY',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastDeletedSubscriptionInstallmentsId: subscriptionId,
                          ...setPropLoaded('deleteSubscriptionInstallments'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError(
                            'deleteSubscriptionInstallments',
                            error
                          ),
                        });
                      },
                    })
                  );
              })
            )
          )
        )
      );

      type TGetSubscriptionsParams = {
        personId?: string | null;
        filter?: number | null;
      };

      const getSubscriptionsByPerson = rxMethod<
        string | null | undefined | TGetSubscriptionsParams
      >(
        pipe(
          tap(() =>
            patchState(store, {
              subscriptions: [],
              // Invalidate all detailed caches before reloading the list. This
              // guarantees dependents tooltip data is refreshed after dependent
              // add/remove operations made in the edit flow.
              currentSubscription: null,
              subscriptionPauses: [],
              consumptionReleases: [],
              subscriptionHasDependentsById: {},
              ...setPropLoading('getSubscriptionsByPerson'),
            })
          ),
          switchMap((params) => {
            const normalizedParams: TGetSubscriptionsParams =
              typeof params === 'object' &&
              params !== null &&
              !Array.isArray(params)
                ? params
                : { personId: params as string | null | undefined };

            const personId = normalizedParams.personId || store.personId();

            if (!personId) {
              const error = new Error('Missing personId');
              patchState(store, {
                ...setPropError('getSubscriptionsByPerson', error),
              });
              errorStore.handleError({
                error,
                title:
                  'Não foi possível identificar a pessoa para listar assinaturas.',
              });
              return EMPTY;
            }

            const filterValue =
              normalizedParams.filter ?? store.subscriptionFilter() ?? 0;

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaObterTodos],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_LIST.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_LIST.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    subscriptions: [],
                    currentSubscription: null,
                    subscriptionPauses: [],
                    consumptionReleases: [],
                    subscriptionHasDependentsById: {},
                    ...setPropError(
                      'getSubscriptionsByPerson',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .getSubscriptionsByPerson(personId, filterValue)
                  .pipe(
                    tapResponse({
                      next: (subscriptions) => {
                        patchState(store, {
                          personId,
                          subscriptions,
                          subscriptionFilter: filterValue,
                          ...setPropLoaded('getSubscriptionsByPerson'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);

                        patchState(store, {
                          ...setPropError('getSubscriptionsByPerson', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      /**
       * Existence check used before converting a person to user: a person with
       * active or paused subscriptions must close them first. Runs the Active and
       * Paused list queries in parallel and stores only the boolean outcome in
       * `hasActiveOrPausedSubscriptions` — it deliberately does NOT touch the
       * `subscriptions` list state, so it can be triggered without clobbering an
       * open subscriptions screen. Consumers react via the request-state flags
       * (`checkActiveOrPausedSubscriptionsLoaded`/`...Error`).
       */
      const checkActiveOrPausedSubscriptions = rxMethod<
        string | null | undefined
      >(
        pipe(
          tap(() =>
            patchState(store, {
              hasActiveOrPausedSubscriptions: null,
              ...setPropLoading('checkActiveOrPausedSubscriptions'),
            })
          ),
          switchMap((personIdParam) => {
            const personId = personIdParam || store.personId();

            if (!personId) {
              const error = new Error('Missing personId');
              patchState(store, {
                ...setPropError('checkActiveOrPausedSubscriptions', error),
              });
              return EMPTY;
            }

            return forkJoin([
              profileSubscriptionService.getSubscriptionsByPerson(
                personId,
                ESubscriptionsByPersonFieldFilter.Active
              ),
              profileSubscriptionService.getSubscriptionsByPerson(
                personId,
                ESubscriptionsByPersonFieldFilter.Paused
              ),
            ]).pipe(
              tapResponse({
                next: ([active, paused]) => {
                  patchState(store, {
                    hasActiveOrPausedSubscriptions: Boolean(
                      active?.length || paused?.length
                    ),
                    ...setPropLoaded('checkActiveOrPausedSubscriptions'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, {
                    ...setPropError('checkActiveOrPausedSubscriptions', error),
                  });
                },
              })
            );
          })
        )
      );

      const getSubscriptionById = rxMethod<string | null | undefined>(
        pipe(
          tap(() =>
            patchState(store, {
              ...setPropLoading('getSubscriptionById'),
            })
          ),
          switchMap((subscriptionIdParam) => {
            const subscriptionId =
              subscriptionIdParam ||
              store.currentSubscription()?.SubscriptionId ||
              null;

            if (!subscriptionId) {
              const error = new Error('Missing subscriptionId');
              patchState(store, {
                ...setPropError('getSubscriptionById', error),
              });
              errorStore.handleError({
                error,
                title:
                  'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaObterPorId],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_VIEW.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_VIEW.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    currentSubscription: null,
                    subscriptionPauses: [],
                    ...setPropError(
                      'getSubscriptionById',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .getSubscriptionById(subscriptionId)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        patchState(store, {
                          currentSubscription: response,
                          subscriptionPauses: fixEndOnUtcPauses(
                            response?.Pauses ?? []
                          ),
                          subscriptionHasDependentsById: {
                            ...store.subscriptionHasDependentsById(),
                            [response.SubscriptionId]:
                              (response.Dependents ?? []).length > 0,
                          },
                          ...setPropLoaded('getSubscriptionById'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        patchState(store, {
                          currentSubscription: null,
                          subscriptionPauses: [],
                          ...setPropError('getSubscriptionById', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      /**
       * Ids cujo preload de `Dependents` está em voo agora. O `filter` abaixo
       * só consegue deduplicar contra o que já respondeu (`...ById`), então sem
       * este set dois cards do mesmo id disparados no mesmo tick fariam duas
       * requisições.
       */
      const inFlightDependentsFlagIds = new Set<string>();

      /**
       * Lightweight preload: ensures `subscriptionHasDependentsById[id]` is
       * populated. Skips if the flag for that id is already known. Does not
       * touch `currentSubscription` so multiple holder cards can run in
       * parallel without overwriting each other.
       *
       * `mergeMap` (e não `switchMap`) é obrigatório aqui: o store é
       * `providedIn: 'root'`, então TODOS os cards de assinatura compartilham
       * este mesmo stream. Cada card titular chama este método no seu próprio
       * effect de preload, no mesmo tick — com `switchMap` o RxJS cancelava
       * toda requisição anterior e só o último card chegava a gravar sua flag.
       * Resultado: `hasDependents()` ficava `false` nos demais e o badge
       * "Responsável" + o tooltip de dependentes sumiam de todos menos um.
       */
      const ensureSubscriptionHasDependentsFlag = rxMethod<
        string | null | undefined
      >(
        pipe(
          filter((subscriptionId): subscriptionId is string => {
            if (!subscriptionId) return false;
            if (inFlightDependentsFlagIds.has(subscriptionId)) return false;
            return !(subscriptionId in store.subscriptionHasDependentsById());
          }),
          tap((subscriptionId) =>
            inFlightDependentsFlagIds.add(subscriptionId)
          ),
          mergeMap(
            (subscriptionId) =>
              // Verificação não interativa: é pré-carga de fundo, não pode abrir
              // o modal de autorização enquanto os cards são renderizados.
              from(
                selfUserStore.ensurePermissions(
                  [PermissionNumber.AssinaturaPessoaObterPorId],
                  { interactive: false }
                )
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) return EMPTY;

                  return profileSubscriptionService
                    .getSubscriptionById(subscriptionId)
                    .pipe(
                      tapResponse({
                        next: (response) => {
                          patchState(store, {
                            subscriptionHasDependentsById: {
                              ...store.subscriptionHasDependentsById(),
                              [subscriptionId]:
                                (response?.Dependents ?? []).length > 0,
                            },
                          });
                        },
                        error: () => {
                          // Silent: preload failure should not block the card.
                        },
                      })
                    );
                }),
                finalize(() =>
                  inFlightDependentsFlagIds.delete(subscriptionId)
                )
              ),
            DEPENDENTS_FLAG_PRELOAD_CONCURRENCY
          )
        )
      );

      const addSubscriptionPause = rxMethod<AddPauseProfileSubscriptionPayload>(
        pipe(
          tap(() =>
            patchState(store, {
              lastPausedSubscription: null,
              ...setPropLoading('addSubscriptionPause'),
            })
          ),
          switchMap((payload) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaAdicionarTrancamento],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PAUSE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PAUSE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    lastPausedSubscription: null,
                    ...setPropError(
                      'addSubscriptionPause',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                if (!payload?.SubscriptionId) {
                  const error = new Error('Missing SubscriptionId for pause');
                  patchState(store, {
                    ...setPropError('addSubscriptionPause', error),
                  });
                  errorStore.handleError({
                    error,
                    title:
                      'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION',
                  });
                  return EMPTY;
                }

                if (!payload?.StartOnUtc) {
                  const error = new Error('Missing StartOnUtc for pause');
                  patchState(store, {
                    ...setPropError('addSubscriptionPause', error),
                  });
                  errorStore.handleError({
                    error,
                    title:
                      'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_START_ON',
                  });
                  return EMPTY;
                }

                if (!payload?.Motivation) {
                  const error = new Error('Missing Motivation for pause');
                  patchState(store, {
                    ...setPropError('addSubscriptionPause', error),
                  });
                  errorStore.handleError({
                    error,
                    title: 'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_REASON',
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .addSubscriptionPause(payload)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        alertStore.create({
                          message:
                            'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.ADD_SUCCESS',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastPausedSubscription: response,
                          currentSubscription: response,
                          subscriptionPauses: fixEndOnUtcPauses(
                            response?.Pauses ?? []
                          ),
                          ...setPropLoaded('addSubscriptionPause'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('addSubscriptionPause', error),
                        });
                      },
                    })
                  );
              })
            )
          )
        )
      );

      const updateSubscriptionPause =
        rxMethod<UpdatePauseProfileSubscriptionPayload>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('updateSubscriptionPause'),
              })
            ),
            switchMap((payload) => {
              if (!payload?.SubscriptionId) {
                const error = new Error('Missing SubscriptionId for pause');
                patchState(store, {
                  ...setPropError('updateSubscriptionPause', error),
                });
                errorStore.handleError({
                  error,
                  title:
                    'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION',
                });
                return EMPTY;
              }

              if (!payload?.SubscriptionPauseId) {
                const error = new Error(
                  'Missing SubscriptionPauseId for pause'
                );
                patchState(store, {
                  ...setPropError('updateSubscriptionPause', error),
                });
                errorStore.handleError({
                  error,
                  title: 'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_PAUSE',
                });
                return EMPTY;
              }

              if (!payload?.StartOnUtc) {
                const error = new Error('Missing StartOnUtc for pause');
                patchState(store, {
                  ...setPropError('updateSubscriptionPause', error),
                });
                errorStore.handleError({
                  error,
                  title: 'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_START_ON',
                });
                return EMPTY;
              }

              if (!payload?.Motivation) {
                const error = new Error('Missing Motivation for pause');
                patchState(store, {
                  ...setPropError('updateSubscriptionPause', error),
                });
                errorStore.handleError({
                  error,
                  title: 'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_REASON',
                });
                return EMPTY;
              }

              return from(
                selfUserStore.ensurePermissions(
                  [PermissionNumber.AssinaturaPessoaEditarTrancamento],
                  {
                    title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_EDIT.TITLE`,
                    message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_EDIT.MESSAGE`,
                  }
                )
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'updateSubscriptionPause',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  return profileSubscriptionService
                    .updateSubscriptionPause(payload)
                    .pipe(
                      tapResponse({
                        next: (response) => {
                          alertStore.create({
                            message:
                              'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.UPDATE_SUCCESS',
                            title: 'ALERT.TITLE_SUCCESS',
                            type: 'success',
                          });
                          patchState(store, {
                            lastPausedSubscription: response,
                            currentSubscription: response,
                            subscriptionPauses: fixEndOnUtcPauses(
                              response?.Pauses ?? []
                            ),
                            ...setPropLoaded('updateSubscriptionPause'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          errorStore.setHttpError(error);
                          patchState(store, {
                            ...setPropError('updateSubscriptionPause', error),
                          });
                        },
                      })
                    );
                })
              );
            })
          )
        );

      const deleteSubscriptionPause =
        rxMethod<DeletePauseProfileSubscriptionPayload>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('deleteSubscriptionPause'),
              })
            ),
            switchMap((payload) => {
              if (!payload?.SubscriptionId) {
                const error = new Error('Missing SubscriptionId for pause');
                patchState(store, {
                  ...setPropError('deleteSubscriptionPause', error),
                });
                errorStore.handleError({
                  error,
                  title:
                    'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_SUBSCRIPTION',
                });
                return EMPTY;
              }

              if (!payload?.SubscriptionPauseId) {
                const error = new Error(
                  'Missing SubscriptionPauseId for pause'
                );
                patchState(store, {
                  ...setPropError('deleteSubscriptionPause', error),
                });
                errorStore.handleError({
                  error,
                  title: 'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.MISSING_PAUSE',
                });
                return EMPTY;
              }

              return from(
                selfUserStore.ensurePermissions(
                  [PermissionNumber.AssinaturaPessoaRemoverTrancamento],
                  {
                    title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_DELETE.TITLE`,
                    message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_PAUSE_DELETE.MESSAGE`,
                  }
                )
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'deleteSubscriptionPause',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  return profileSubscriptionService
                    .deleteSubscriptionPause(payload)
                    .pipe(
                      tapResponse({
                        next: (response) => {
                          alertStore.create({
                            message:
                              'PROFILE.SUBSCRIPTION.PAUSES.ALERTS.DELETE_SUCCESS',
                            title: 'ALERT.TITLE_SUCCESS',
                            type: 'success',
                          });
                          patchState(store, {
                            lastPausedSubscription: response,
                            currentSubscription: response,
                            subscriptionPauses: fixEndOnUtcPauses(
                              response?.Pauses ?? []
                            ),
                            ...setPropLoaded('deleteSubscriptionPause'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          errorStore.setHttpError(error);
                          patchState(store, {
                            ...setPropError('deleteSubscriptionPause', error),
                          });
                        },
                      })
                    );
                })
              );
            })
          )
        );

      const switchSubscription = rxMethod<SwitchProfileSubscriptionPayload>(
        pipe(
          tap(() =>
            patchState(store, {
              lastSwitchedSubscription: null,
              ...setPropLoading('switchSubscription'),
            })
          ),
          switchMap((payload) => {
            if (!payload?.subscriptionId) {
              const error = new Error('Missing subscriptionId for switch');
              patchState(store, {
                ...setPropError('switchSubscription', error),
              });
              errorStore.handleError({
                error,
                title:
                  'Não foi possível identificar a assinatura para transferir.',
              });
              return EMPTY;
            }

            if (!payload?.destinationPersonId) {
              const error = new Error('Missing destinationPersonId for switch');
              patchState(store, {
                ...setPropError('switchSubscription', error),
              });
              errorStore.handleError({
                error,
                title: 'Informe a pessoa de destino para a transferência.',
              });
              return EMPTY;
            }

            if (!payload?.destinationCycleId) {
              const error = new Error('Missing destinationCycleId for switch');
              patchState(store, {
                ...setPropError('switchSubscription', error),
              });
              errorStore.handleError({
                error,
                title: 'Informe o ciclo de destino para a transferência.',
              });
              return EMPTY;
            }

            if (!payload?.transferOnUtc) {
              const error = new Error('Missing transferOnUtc for switch');
              patchState(store, {
                ...setPropError('switchSubscription', error),
              });
              errorStore.handleError({
                error,
                title: 'Informe a data da transferência.',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaAtualizar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    lastSwitchedSubscription: null,
                    ...setPropError(
                      'switchSubscription',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .switchSubscription(payload)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        alertStore.create({
                          message: 'SUBSCRIPTION.ALERTS.SWAPPED.SUCCESSFFULY',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastSwitchedSubscription: response,
                          ...setPropLoaded('switchSubscription'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('switchSubscription', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      const swapOwnership = rxMethod<SwapOwnershipProfileSubscriptionPayload>(
        pipe(
          tap(() =>
            patchState(store, {
              lastSwapOwnershipSubscription: null,
              ...setPropLoading('swapOwnership'),
            })
          ),
          switchMap((payload) => {
            if (!payload?.SubscriptionId) {
              const error = new Error(
                'Missing SubscriptionId for swap ownership'
              );
              patchState(store, { ...setPropError('swapOwnership', error) });
              errorStore.handleError({
                error,
                title:
                  'Não foi possível identificar a assinatura para alterar a titularidade.',
              });
              return EMPTY;
            }

            if (!payload?.NewHolderPersonId) {
              const error = new Error(
                'Missing NewHolderPersonId for swap ownership'
              );
              patchState(store, { ...setPropError('swapOwnership', error) });
              errorStore.handleError({
                error,
                title:
                  'Informe a pessoa que será o novo titular da assinatura.',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaAtualizar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    lastSwapOwnershipSubscription: null,
                    ...setPropError(
                      'swapOwnership',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService.swapOwnership(payload).pipe(
                  tapResponse({
                    next: (response) => {
                      alertStore.create({
                        message: 'SUBSCRIPTION.ALERTS.SWAP_OWNERSHIP.SUCCESS',
                        title: 'ALERT.TITLE_SUCCESS',
                        type: 'success',
                      });
                      patchState(store, {
                        lastSwapOwnershipSubscription: response,
                        ...setPropLoaded('swapOwnership'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, {
                        ...setPropError('swapOwnership', error),
                      });
                    },
                  })
                );
              })
            );
          })
        )
      );

      /**
       * Troca ou retira o responsável financeiro. A permissão é a mesma da
       * titularidade. 404 e 409 não passam pelo `ErrorStore` (ele não tem
       * mensagem para 409): viram `financialResponsibleGone` e um alerta
       * próprio, e a tela fecha a modal e recarrega. O resto (400) mostra a
       * `Message` do servidor e mantém a modal aberta.
       */
      const changeFinancialResponsible =
        rxMethod<ChangeFinancialResponsiblePayload>(
          pipe(
            // `exhaustMap`: é uma escrita. Um segundo disparo durante o PATCH é
            // ignorado — com `switchMap` ele descartava a resposta do primeiro
            // (já aplicado no servidor) e as contas presas nunca apareciam.
            exhaustMap(({ subscriptionId, request }) => {
              patchState(store, {
                lastFinancialResponsibleChange: null,
                financialResponsibleGone: null,
                ...setPropLoading('changeFinancialResponsible'),
              });

              if (!subscriptionId) {
                patchState(store, {
                  ...setPropError(
                    'changeFinancialResponsible',
                    new Error('Missing SubscriptionId')
                  ),
                });
                errorStore.handleError({
                  message: 'SUBSCRIPTION.FINANCIAL_RESPONSIBLE.ERRORS.NOT_FOUND',
                });
                return EMPTY;
              }

              return from(
                selfUserStore.ensurePermissions(
                  [PermissionNumber.AssinaturaPessoaAtualizar],
                  {
                    title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.TITLE`,
                    message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_UPDATE.MESSAGE`,
                  }
                )
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'changeFinancialResponsible',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  return profileSubscriptionService
                    .changeFinancialResponsible(subscriptionId, request)
                    .pipe(
                      tapResponse({
                        next: (response) => {
                          alertStore.create({
                            message:
                              'SUBSCRIPTION.ALERTS.FINANCIAL_RESPONSIBLE.SUCCESS',
                            title: 'ALERT.TITLE_SUCCESS',
                            type: 'success',
                          });
                          patchState(store, {
                            lastFinancialResponsibleChange: response,
                            ...setPropLoaded('changeFinancialResponsible'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          const gone =
                            error?.status === 404
                              ? 'notFound'
                              : error?.status === 409
                              ? 'closed'
                              : null;

                          if (gone) {
                            alertStore.create({
                              message:
                                gone === 'notFound'
                                  ? 'SUBSCRIPTION.FINANCIAL_RESPONSIBLE.ERRORS.NOT_FOUND'
                                  : 'SUBSCRIPTION.FINANCIAL_RESPONSIBLE.ERRORS.CLOSED',
                              title: 'ALERT.TITLE_ERROR',
                              type: 'danger',
                            });
                          } else {
                            errorStore.setHttpError(error);
                          }

                          patchState(store, {
                            financialResponsibleGone: gone,
                            ...setPropError(
                              'changeFinancialResponsible',
                              error
                            ),
                          });
                        },
                      })
                    );
                })
              );
            })
          )
        );

      const loadConsumptionReleases = rxMethod<string | null | undefined>(
        pipe(
          tap(() =>
            patchState(store, {
              consumptionReleases: [],
              ...setPropLoading('loadConsumptionReleases'),
            })
          ),
          switchMap((subscriptionIdParam) => {
            const subscriptionId = subscriptionIdParam?.trim() || null;

            if (!subscriptionId) {
              const error = new Error(
                'Missing subscriptionId for consumption releases'
              );
              patchState(store, {
                ...setPropError('loadConsumptionReleases', error),
              });
              errorStore.handleError({
                error,
                title:
                  'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.MISSING_SUBSCRIPTION',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaPessoaObterPorId],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_VIEW.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_VIEW.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    consumptionReleases: [],
                    ...setPropError(
                      'loadConsumptionReleases',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .getConsumptionReleases(subscriptionId)
                  .pipe(
                    tapResponse({
                      next: (releases) => {
                        patchState(store, {
                          consumptionReleases: releases,
                          ...setPropLoaded('loadConsumptionReleases'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.handleError({
                          error,
                          title:
                            'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.LOAD_ERROR',
                        });
                        patchState(store, {
                          consumptionReleases: [],
                          ...setPropError('loadConsumptionReleases', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      const createConsumptionRelease =
        rxMethod<CreateConsumptionReleasePayload>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('createConsumptionRelease'),
              })
            ),
            switchMap((payload) =>
              from(
                selfUserStore.ensurePermissions(
                  [PermissionNumber.LiberacaoAssinaturaPessoaAdicionar],
                  {
                    title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RELEASE_ADD.TITLE`,
                    message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RELEASE_ADD.MESSAGE`,
                  }
                )
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'createConsumptionRelease',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  if (!payload?.SubscriptionId) {
                    const error = new Error(
                      'Missing SubscriptionId for consumption release'
                    );
                    patchState(store, {
                      ...setPropError('createConsumptionRelease', error),
                    });
                    errorStore.handleError({
                      error,
                      title:
                        'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.MISSING_SUBSCRIPTION',
                    });
                    return EMPTY;
                  }

                  if (!payload?.StartOnUtc || !payload?.EndOnUtc) {
                    const error = new Error(
                      'Missing period for consumption release'
                    );
                    patchState(store, {
                      ...setPropError('createConsumptionRelease', error),
                    });
                    errorStore.handleError({
                      error,
                      title:
                        'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.MISSING_PERIOD',
                    });
                    return EMPTY;
                  }

                  const description = (payload.Description ?? '').trim();
                  if (!description) {
                    const error = new Error(
                      'Missing Description for consumption release'
                    );
                    patchState(store, {
                      ...setPropError('createConsumptionRelease', error),
                    });
                    errorStore.handleError({
                      error,
                      title:
                        'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.MISSING_DESCRIPTION',
                    });
                    return EMPTY;
                  }

                  if (description.length > 100) {
                    const error = new Error(
                      'Description too long for consumption release'
                    );
                    patchState(store, {
                      ...setPropError('createConsumptionRelease', error),
                    });
                    errorStore.handleError({
                      error,
                      title:
                        'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.DESCRIPTION_TOO_LONG',
                    });
                    return EMPTY;
                  }

                  const body: CreateConsumptionReleasePayload = {
                    StartOnUtc: payload.StartOnUtc,
                    EndOnUtc: payload.EndOnUtc,
                    Description: description,
                    SubscriptionId: payload.SubscriptionId,
                  };

                  return profileSubscriptionService
                    .createConsumptionRelease(body)
                    .pipe(
                      switchMap(() =>
                        profileSubscriptionService.getConsumptionReleases(
                          payload.SubscriptionId
                        )
                      ),
                      tapResponse({
                        next: (releases) => {
                          alertStore.create({
                            message:
                              'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.CREATE_SUCCESS',
                            title: 'ALERT.TITLE_SUCCESS',
                            type: 'success',
                          });
                          patchState(store, {
                            consumptionReleases: releases,
                            consumptionReleasesBySubscriptionId: {
                              ...store.consumptionReleasesBySubscriptionId(),
                              [payload.SubscriptionId]: releases,
                            },
                            ...setPropLoaded('createConsumptionRelease'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          errorStore.setHttpError(error);
                          patchState(store, {
                            ...setPropError('createConsumptionRelease', error),
                          });
                        },
                      })
                    );
                })
              )
            )
          )
        );

      const deleteConsumptionRelease =
        rxMethod<DeleteConsumptionReleasePayload>(
          pipe(
            tap(() =>
              patchState(store, {
                ...setPropLoading('deleteConsumptionRelease'),
              })
            ),
            switchMap((payload) =>
              from(
                selfUserStore.ensurePermissions(
                  [PermissionNumber.LiberacaoAssinaturaPessoaRemover],
                  {
                    title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RELEASE_DELETE.TITLE`,
                    message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_PERSON_RELEASE_DELETE.MESSAGE`,
                  }
                )
              ).pipe(
                switchMap((hasPermission) => {
                  if (!hasPermission) {
                    patchState(store, {
                      ...setPropError(
                        'deleteConsumptionRelease',
                        new Error('ALERT.ERROR_401')
                      ),
                    });
                    return EMPTY;
                  }

                  if (!payload?.ConsumptionReleaseId?.trim()) {
                    const error = new Error(
                      'Missing ConsumptionReleaseId for consumption release delete'
                    );
                    patchState(store, {
                      ...setPropError('deleteConsumptionRelease', error),
                    });
                    errorStore.handleError({
                      error,
                      title:
                        'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.MISSING_RELEASE_ID',
                    });
                    return EMPTY;
                  }

                  if (!payload?.SubscriptionId?.trim()) {
                    const error = new Error(
                      'Missing SubscriptionId for consumption release delete'
                    );
                    patchState(store, {
                      ...setPropError('deleteConsumptionRelease', error),
                    });
                    errorStore.handleError({
                      error,
                      title:
                        'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.MISSING_SUBSCRIPTION',
                    });
                    return EMPTY;
                  }

                  const consumptionReleaseId =
                    payload.ConsumptionReleaseId.trim();
                  const subscriptionId = payload.SubscriptionId.trim();

                  return profileSubscriptionService
                    .deleteConsumptionRelease(consumptionReleaseId)
                    .pipe(
                      switchMap(() =>
                        profileSubscriptionService.getConsumptionReleases(
                          subscriptionId
                        )
                      ),
                      tapResponse({
                        next: (releases) => {
                          alertStore.create({
                            message:
                              'PROFILE.SUBSCRIPTION.CONSUMPTION_RELEASES.ALERTS.DELETE_SUCCESS',
                            title: 'ALERT.TITLE_SUCCESS',
                            type: 'success',
                          });
                          patchState(store, {
                            consumptionReleases: releases,
                            consumptionReleasesBySubscriptionId: {
                              ...store.consumptionReleasesBySubscriptionId(),
                              [subscriptionId]: releases,
                            },
                            ...setPropLoaded('deleteConsumptionRelease'),
                          });
                        },
                        error: (error: HttpErrorResponse) => {
                          errorStore.setHttpError(error);
                          patchState(store, {
                            ...setPropError('deleteConsumptionRelease', error),
                          });
                        },
                      })
                    );
                })
              )
            )
          )
        );

      const transferSubscription = rxMethod<TransferProfileSubscriptionPayload>(
        pipe(
          tap(() =>
            patchState(store, {
              lastTransferredSubscription: null,
              ...setPropLoading('transferSubscription'),
            })
          ),
          switchMap((payload) => {
            if (!payload?.SubscriptionId) {
              const error = new Error('Missing SubscriptionId for transfer');
              patchState(store, {
                ...setPropError('transferSubscription', error),
              });
              errorStore.handleError({
                error,
                title:
                  'Não foi possível identificar a assinatura para transferir.',
              });
              return EMPTY;
            }

            if (!payload?.DestinationPersonId) {
              const error = new Error(
                'Missing DestinationPersonId for transfer'
              );
              patchState(store, {
                ...setPropError('transferSubscription', error),
              });
              errorStore.handleError({
                error,
                title: 'Informe a pessoa de destino para a transferência.',
              });
              return EMPTY;
            }

            if (!payload?.DestinationCycleId) {
              const error = new Error(
                'Missing DestinationCycleId for transfer'
              );
              patchState(store, {
                ...setPropError('transferSubscription', error),
              });
              errorStore.handleError({
                error,
                title: 'Informe o ciclo de destino para a transferência.',
              });
              return EMPTY;
            }

            if (!payload?.TransferOnUtc) {
              const error = new Error('Missing TransferOnUtc for transfer');
              patchState(store, {
                ...setPropError('transferSubscription', error),
              });
              errorStore.handleError({
                error,
                title: 'Informe a data da transferência.',
              });
              return EMPTY;
            }

            return from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AdicionarAssinaturaPessoaTransferencia],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_TRANSFER_ADD.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_TRANSFER_ADD.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(store, {
                    lastTransferredSubscription: null,
                    ...setPropError(
                      'transferSubscription',
                      new Error('ALERT.ERROR_401')
                    ),
                  });
                  return EMPTY;
                }

                return profileSubscriptionService
                  .transferSubscription(payload)
                  .pipe(
                    tapResponse({
                      next: (response) => {
                        alertStore.create({
                          message:
                            'SUBSCRIPTION.ALERTS.TRANSFERRED.SUCCESSFFULY',
                          title: 'ALERT.TITLE_SUCCESS',
                          type: 'success',
                        });
                        patchState(store, {
                          lastTransferredSubscription: response,
                          ...setPropLoaded('transferSubscription'),
                        });
                      },
                      error: (error: HttpErrorResponse) => {
                        errorStore.setHttpError(error);
                        patchState(store, {
                          ...setPropError('transferSubscription', error),
                        });
                      },
                    })
                  );
              })
            );
          })
        )
      );

      const inFlightOpenReceivableIds = new Set<string>();

      /**
       * Loads (or refreshes) the open account receivables for a subscription
       * into `openReceivablesBySubscriptionId[id]`, from the OData feed. Failures
       * are silent so a card never breaks.
       *
       * `mergeMap` (e não `switchMap`) pelo mesmo motivo de
       * `ensureSubscriptionHasDependentsFlag`: o store é `providedIn: 'root'`,
       * então TODOS os cards compartilham este stream e cada um chama o método
       * no seu próprio effect de preload, no mesmo tick. Com `switchMap` o RxJS
       * desinscrevia a requisição anterior — o `HttpClient` abortava o XHR já
       * com os headers recebidos (daí o 200 sem corpo no DevTools) e só o
       * último card gravava seus recebíveis; os demais ficavam presos em
       * `undefined`, ou seja, em estado de carregamento eterno.
       *
       * O `Set` de ids em voo apenas descarta chamadas duplicadas para a MESMA
       * assinatura enquanto uma está aberta; como o `finalize` limpa o id, o
       * refresh do flip (`onFlipRequested`) continua funcionando.
       */
      const loadOpenReceivables = rxMethod<string | null | undefined>(
        pipe(
          filter((subscriptionId): subscriptionId is string => {
            if (!subscriptionId) return false;
            return !inFlightOpenReceivableIds.has(subscriptionId);
          }),
          tap((subscriptionId) =>
            inFlightOpenReceivableIds.add(subscriptionId)
          ),
          mergeMap(
            (subscriptionId) =>
              profileSubscriptionService
                .getOpenReceivablesBySubscription(subscriptionId)
                .pipe(
                  tapResponse({
                    next: (receivables) => {
                      patchState(store, {
                        openReceivablesBySubscriptionId: {
                          ...store.openReceivablesBySubscriptionId(),
                          [subscriptionId]: receivables ?? [],
                        },
                      });
                    },
                    error: () => {
                      // Silent: a receivables-load failure should not block the card.
                    },
                  }),
                  finalize(() =>
                    inFlightOpenReceivableIds.delete(subscriptionId)
                  )
                ),
            OPEN_RECEIVABLES_PRELOAD_CONCURRENCY
          )
        )
      );

      const inFlightConsumptionReleaseIds = new Set<string>();

      /**
       * Loads one subscription's releases for its card, without touching the
       * modal's `consumptionReleases`. Unlike `loadConsumptionReleases` it runs on
       * page load, so it neither asks for permission (that opens the temporary
       * authorization modal) nor raises an alert: the OData feed only requires a
       * session, and a failure just leaves the card showing the overdue state.
       *
       * `mergeMap` for the reason given on `loadOpenReceivables`: every card shares
       * this stream, and `switchMap` would abort all but the last request.
       */
      const loadSubscriptionConsumptionReleases = rxMethod<
        string | null | undefined
      >(
        pipe(
          filter((subscriptionId): subscriptionId is string => {
            if (!subscriptionId) return false;
            return !inFlightConsumptionReleaseIds.has(subscriptionId);
          }),
          tap((subscriptionId) =>
            inFlightConsumptionReleaseIds.add(subscriptionId)
          ),
          mergeMap(
            (subscriptionId) =>
              profileSubscriptionService
                .getConsumptionReleases(subscriptionId)
                .pipe(
                  tapResponse({
                    next: (releases) => {
                      patchState(store, {
                        consumptionReleasesBySubscriptionId: {
                          ...store.consumptionReleasesBySubscriptionId(),
                          [subscriptionId]: releases,
                        },
                      });
                    },
                    error: () => {
                      // Silent: a release-load failure should not block the card.
                    },
                  }),
                  finalize(() =>
                    inFlightConsumptionReleaseIds.delete(subscriptionId)
                  )
                ),
            CONSUMPTION_RELEASES_PRELOAD_CONCURRENCY
          )
        )
      );

      const fixEndOnUtcPauses = (
        pauses: TSubscriptionPause[]
      ): TSubscriptionPause[] => {
        return (pauses ?? [])
          .map((p) => {
            if (!p) return null;
            const endOnUtc = getDateTime(p.StartOnUtc)
              ?.plus({ days: (p.Days ?? 1) - 1 })
              .endOf('day');
            return {
              ...p,
              EndOnUtc: endOnUtc?.isValid
                ? endOnUtc.toISO() ?? p.EndOnUtc
                : p.EndOnUtc,
            };
          })
          .filter((p): p is TSubscriptionPause => !!p);
      };

      return {
        setPersonId: (personId: string | null) => {
          patchState(store, { personId });
        },
        clearRenewedSubscription: () => {
          patchState(
            store,
            { lastRenewedSubscription: null },
            setPropInit('renewSubscription')
          );
        },
        clearClosedSubscription: () => {
          patchState(
            store,
            { lastClosedSubscription: null },
            setPropInit('closeSubscription')
          );
        },
        clearPausedSubscription: () => {
          patchState(store, { lastPausedSubscription: null });
        },
        clearSwitchedSubscription: () => {
          patchState(
            store,
            { lastSwitchedSubscription: null },
            setPropInit('switchSubscription')
          );
        },
        clearTransferredSubscription: () => {
          patchState(
            store,
            { lastTransferredSubscription: null },
            setPropInit('transferSubscription')
          );
        },
        clearSwapOwnershipSubscription: () => {
          patchState(
            store,
            { lastSwapOwnershipSubscription: null },
            setPropInit('swapOwnership')
          );
        },
        clearFinancialResponsibleChange: () => {
          patchState(
            store,
            {
              lastFinancialResponsibleChange: null,
              financialResponsibleGone: null,
            },
            setPropInit('changeFinancialResponsible')
          );
        },
        clearCurrentSubscription: () => {
          patchState(
            store,
            {
              currentSubscription: null,
              subscriptionPauses: [],
              consumptionReleases: [],
            },
            setPropInit('getSubscriptionById')
          );
        },
        clearDeletedSubscription: () => {
          patchState(
            store,
            { lastDeletedSubscriptionId: null },
            setPropInit('deleteSubscriptionById')
          );
        },
        clearDeletedSubscriptionRenewals: () => {
          patchState(
            store,
            { lastDeletedSubscriptionRenewalsId: null },
            setPropInit('deleteSubscriptionRenewals')
          );
        },
        clearDeletedSubscriptionInstallments: () => {
          patchState(
            store,
            { lastDeletedSubscriptionInstallmentsId: null },
            setPropInit('deleteSubscriptionInstallments')
          );
        },
        clearCanceledAutomaticRenewal: () => {
          patchState(
            store,
            { lastAutoRenewalCanceledSubscription: null },
            setPropInit('cancelAutomaticRenewal')
          );
        },
        clearSubscriptionsByPerson: () => {
          patchState(
            store,
            { subscriptions: [] },
            setPropInit('getSubscriptionsByPerson')
          );
        },
        clearActiveOrPausedSubscriptionsCheck: () => {
          patchState(
            store,
            { hasActiveOrPausedSubscriptions: null },
            setPropInit('checkActiveOrPausedSubscriptions')
          );
        },
        initProps: (...props: string[]) => {
          patchState(store, { ...setPropInit(...props) });
        },
        setPropsInit(props: string[]): void {
          patchState(store, setPropInit(...props));
        },
        renewSubscription,
        closeSubscription,
        getSubscriptionById,
        loadOpenReceivables,
        ensureSubscriptionHasDependentsFlag,
        addSubscriptionPause,
        updateSubscriptionPause,
        deleteSubscriptionPause,
        switchSubscription,
        swapOwnership,
        changeFinancialResponsible,
        transferSubscription,
        deleteSubscriptionById,
        deleteSubscriptionRenewals,
        cancelAutomaticRenewal,
        deleteSubscriptionInstallments,
        getSubscriptionsByPerson,
        checkActiveOrPausedSubscriptions,
        loadConsumptionReleases,
        loadSubscriptionConsumptionReleases,
        createConsumptionRelease,
        deleteConsumptionRelease,
      };
    }
  ),
  withReqState(
    'renewSubscription',
    'closeSubscription',
    'addSubscriptionPause',
    'updateSubscriptionPause',
    'deleteSubscriptionPause',
    'switchSubscription',
    'swapOwnership',
    'changeFinancialResponsible',
    'transferSubscription',
    'getSubscriptionById',
    'deleteSubscriptionById',
    'deleteSubscriptionRenewals',
    'cancelAutomaticRenewal',
    'deleteSubscriptionInstallments',
    'getSubscriptionsByPerson',
    'checkActiveOrPausedSubscriptions',
    'loadConsumptionReleases',
    'createConsumptionRelease',
    'deleteConsumptionRelease'
  ),
  withComputed((store) => ({
    /**
     * The last delete was refused because the subscription has payments (422
     * "Esta assinatura tem pagamentos. Para interrompê-la, encerre o plano.",
     * for everyone, owner included). The message is already in the alert; this
     * tells the host to lead the user to closing the plan instead.
     */
    deleteSubscriptionBlockedByPayments: computed<boolean>(
      () =>
        store.deleteSubscriptionByIdError() instanceof HttpErrorResponse &&
        store.deleteSubscriptionByIdError().status === 422
    ),
  }))
);
